import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScrollerMotion } from 'scroller-motion';

const SelfbookBookingBarModule = ({ data }) => {
  const router = useRouter();

  const nonSbHotels = data.hotels
    .map((hotel) => ({
      ...hotel,
      id: hotel.hotelName
        .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => (index === 0 ? word.toLowerCase() : word.toUpperCase()))
        .replace(/\s+/g, ''),
      name: hotel.hotelName,
      destination_id: hotel.hotelLocation,
      bookingSystem: hotel.bookingSystem,
    }))
    .filter((hotel) => hotel.bookingSystem !== 'Selfbook');

  const groupHotels = nonSbHotels.map(({ id, name, destination_id }) => ({
    id,
    name,
    destination_id,
  }));

  let scrollY = 0; // Default to 0 if `useScrollerMotion` is not available
  const [scrollTop, setScrollTop] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [showBar, setShowBar] = useState(false);
  const [relative, setRelative] = useState(false);

  try {
    const scrollerMotion = useScrollerMotion();
    scrollY = scrollerMotion?.scrollY || 0; // Update scrollY if available
  } catch (error) {
    // If an error occurs (i.e., ScrollerMotion context is not available), use a fallback
    // console.warn('useScrollerMotion is not available. Using fallback behavior.');
  }

  useEffect(() => {
    setLoaded(true);
    document.querySelector('.page-container-content').classList.add('bg-secondary-coolsand');
    let distanceFromTop = 5000;

    const loaded = () => {
      const relativeBB = document.querySelector('.relative-booking-bar');
      if (!relativeBB) return;
      const distance = window.getComputedStyle(relativeBB).getPropertyValue('--distance');
      const relativeBookingBar = relativeBB.offsetTop - window.innerHeight + parseInt(distance);
      setTimeout(() => {
        distanceFromTop = relativeBookingBar;
        setShowBar(true);
      }, 100);
    };

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setScrollTop(scrollTop);
      if (scrollTop > distanceFromTop) {
        setRelative(true);
      } else {
        setRelative(false);
      }
    };

    if (scrollY) {
      scrollY.onChange((value) => {
        const scrollTop = value;
        if (scrollTop > distanceFromTop) {
          setRelative(true);
        } else {
          setRelative(false);
        }
      });
    }

    const resizeObserver = new ResizeObserver(() => {
      loaded();
    });
    resizeObserver.observe(document.documentElement);

    window.addEventListener('load', loaded);
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('load', loaded);
      resizeObserver.disconnect();
    };
  }, [scrollY]);

  useEffect(() => {
    const handleNonSbHotelsBooking = ({ detail }) => {
      if (detail && detail.eventName === 'book_external_hotel') {
        const selectedNonSbHotel = nonSbHotels.find((hotel) => hotel.id === detail.data.hotelId);

        if (selectedNonSbHotel) {
          const bookingSystem = data?.bookingSystems.find((bs) => bs.name === selectedNonSbHotel.bookingSystem);

          if (bookingSystem) {
            try {
              const { rootUrl } = bookingSystem;

              if (!rootUrl) {
                console.error('bookingSystem.rootUrl is undefined or empty');
                return;
              }

              let formattedUrl;

              try {
                const slash = rootUrl.endsWith('/') ? '' : '/';
                formattedUrl = new URL(rootUrl + slash);
              } catch (err) {
                console.error('Invalid root URL: ', rootUrl);
                return;
              }

              formattedUrl.searchParams.set('locale', router.locale);

              formattedUrl.searchParams.set(bookingSystem.hotelParam, selectedNonSbHotel.bookingCode);

              if (detail.data.startDate) {
                formattedUrl.searchParams.set(bookingSystem.checkInParam, detail.data.startDate);
              }

              if (detail.data.endDate) {
                formattedUrl.searchParams.set(bookingSystem.checkOutParam, detail.data.endDate);
              }

              const guests = detail.data.roomsGuests || detail.data.guests;

              if (guests && Array.isArray(guests) && guests.length) {
                let adults = 0;
                let children = 0;

                guests.forEach((guest) => {
                  if (guest.type === 'adult') {
                    adults += guest.count;
                  }

                  if (guest.type === 'child') {
                    children += guest.count;
                  }
                });

                if (adults) {
                  formattedUrl.searchParams.set(bookingSystem.adultParam, adults);
                }

                if (children) {
                  formattedUrl.searchParams.set(bookingSystem.childParam, children);
                }
              }

              if (detail.data.promoCode) {
                formattedUrl.searchParams.set(bookingSystem.promoCodeParam || 'promoCode', detail.data.promoCode);
              }

              window.open(formattedUrl.href, '_blank');
            } catch (err) {
              console.error('Error constructing URL: ', err);
            }
          } else {
            console.error('Invalid booking system: ', bookingSystem);
          }
        }
      }
    };

    const handler = (e) => handleNonSbHotelsBooking(e);
    window.addEventListener('selfbook_searchbar', handler);

    return () => {
      window.removeEventListener('selfbook_searchbar', handler);
    };
  }, [data, nonSbHotels, router.locale]);

  // Re-initialize the floating searchbar widget on mount.
  // Handles first-load race (SDK may fire renderWidget before #sb_searchbar exists)
  // and SPA navigation (SDK bootstrap only runs once, so renderWidget must be called again)
  useEffect(() => {
    if (!loaded) return;

    let intervalId;
    let cancelled = false;

    const initSearchbar = () => {
      if (typeof window.renderWidget !== 'function') return false;

      const container = document.querySelector('.sb-searchbar');
      if (!container) return false;
      // Already rendered (has children beyond the config script)
      if (container.childElementCount > 1 || container.dataset.sbInitialized) return true;

      const sdkScript = document.getElementById('selfbook_jssdk');
      if (!sdkScript) return false;

      try {
        const url = new URL(sdkScript.getAttribute('src'));
        container.dataset.sbInitialized = 'true';
        window.renderWidget(null, {
          backendURL: 'https://api.selfbook.com/api',
          hotelId: url.searchParams.get('hotelId'),
          apiKey: url.searchParams.get('apiKey'),
        });
      } catch (e) {
        console.error('Selfbook searchbar init error:', e);
        container.dataset.sbInitialized = '';
      }
      return true;
    };

    if (!initSearchbar()) {
      intervalId = setInterval(() => {
        if (cancelled) return;
        if (initSearchbar()) {
          clearInterval(intervalId);
        }
      }, 250);
    }

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [loaded]);

  const sbConfig = {
    fontFamily: 'Cera, Helvetica, sans-serif',
    triggers: {
      shape: 'square',
      type: 'labeled',
      labels: {
        destination: 'Destinations',
        calendar: 'Arrival – Departure',
        guests: 'Guests',
        promo: 'Promo',
      },
      showIconRight: true,
    },
    groupHotels: {
      hotels: [...groupHotels],
    },
    styles: `
      :host {
        --sb-modal-radius: 4px;
        --sb-widget-padding: 8px;
        --sb-widget-radius: 4px;
      }
      .widget-main {
        background-color: rgb(247, 241, 235);
        margin: 0 auto;
        max-width: 1084px;
      }
      .widget--inner {
        gap: 8px;
      }
      .trigger-row {
        width: 0;
      }
      .btn-trigger {
        padding: 10px 21px 10px 24px;
        text-align: left;
      }
      .btn-trigger :has(> .trigger__btn-text) {
        display: grid;
        grid-template-areas:
          "a c"
          "b c";
      }
      .btn-trigger :has(> .trigger__btn-text) :first-child {
        color: #393939;
        font: 400 14px/18px Cera, Helvetica, sans-serif;
        grid-area: a;
        letter-spacing: 0.16px;
        white-space: nowrap;
      }
      .btn-trigger .trigger__btn-text {
        color: #0F0F0F;
        font: 400 16px/22px Cera, Helvetica, sans-serif;
        grid-area: b;
      }
      .btn-trigger .trigger__btn-text ~ i {
        align-self: center;
        grid-area: c;
        height: 20px;
        margin-left: 32px;
        width: 20px;
      }
      .btn-trigger--destination > div {
        margin: 0;
        text-align: left;
      }
      .btn-trigger--destination > i {
        display: none;
      }
      .trigger__btn-text--destination {
        min-height: 1em;
      }
      .btn-trigger--calendar {
        flex-shrink: 1;
      }
      .trigger__btn-text--calendar--selected {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
        width: fit-content;
      }
      .btn--submit,
      .widget-mobile {
        background-color: #FFB12E;
        border-radius: 4px;
        color: #1A1A1A;
        font-size: 16px;
        font-weight: 400;
        height: 60px;
        line-height: 22px;
        padding: 8px 16px;
      }
      :is(.btn--submit, .widget-mobile):not(:disabled):hover {
        background: linear-gradient(0deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.16) 100%), #FFB12E;
      }

      @media screen and (min-width: 1440px) {
        .widget-main {
          max-width: 1360px;
        }
      }
    `,
    theme: {
      ui: {
        button: {
          primary: {
            hover: {
              light: {
                background: '#FF0000',
              },
            },
          },
        },
        calendar: {
          hover: {
            light: {
              bkgHover: 'rgba(71, 118, 157, 0.12)',
            },
          },
          selected: {
            light: {
              arrDepText: '#FFFFFF',
              arrDepBkg: '#47769D',
              midstayBkg: 'rgba(71, 118, 157, 0.24)',
            },
          },
        },
      },
    },
  };

  if (data.currentHotel) {
    Object.assign(sbConfig, { hotelId: data.currentHotel.bookingCode, hotelPreselected: true });
  }

  return (
    loaded && (
      <>
        <motion.div
          className={classNames(
            'booking-bar h-0 w-full z-50 bottom-0 [--distance:60px] md:[--distance:76px]',
            relative ? 'opacity-0' : 'opacity-100',
            navigator.userAgent.indexOf('Firefox') > 0 ||
              /.*Version\/(1[0-4]|[5-9])(\.\d+)* Safari.*/.test(navigator.userAgent)
              ? 'absolute'
              : 'sticky',
          )}
          style={{
            y: scrollY,
            top: scrollTop > 0 ? 'calc(100dvh - var(--distance, 76px))' : 'calc(100dvh - var(--distance, 76px) - 10px)',
            transition: 'margin .5s linear, padding .5s linear, top .5s linear, opacity .3s linear',
          }}
        >
          <div
            style={{ transition: 'opacity .5s linear, width .5s linear' }}
            className={classNames(
              'h-[3.75rem] md:h-[4.75rem] relative has-[div.sb-searchbar>div]:md:bg-secondary-coolsand rounded m-auto',
              scrollTop > 0
                ? 'w-full'
                : 'w-[calc(100%-2rem)] md:w-[calc(100%-3rem)] lg:w-[min(calc(100%-5rem),1084px)] xxl:w-[1360px]',
              !showBar ? 'opacity-0' : 'opacity-100',
            )}
          >
            {data?.currentHotel?.bookingDirectMessage && (
              <div
                className="absolute block left-1/2 has-[+_div.sb-searchbar>div]:opacity-100 opacity-0 transition-all duration-500 -translate-x-1/2 md:translate-x-0 max-w-full md:max-w-[90%] lg:left-2 w-max md:w-fit md:left-[unset] md:right-10 text-primary bottom-[4.5rem] md:bottom-[88px] bg-highlight-sunshineYellow rounded-[48px] border border-primary/10 text-paragraph-s not-italic font-normal py-1 leading-5 [&_a]:py-2 [&_p]:px-6"
                dangerouslySetInnerHTML={{ __html: data?.currentHotel?.bookingDirectMessage }}
              />
            )}
            <div className="sb-searchbar">
              <script
                id="sb_searchbar"
                type="application/json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(sbConfig) }}
              ></script>
            </div>
          </div>
        </motion.div>
        {createPortal(
          <div
            className={`relative-booking-bar booking-bar rounded m-auto transition-width duration-500 [--distance:40px] ${scrollTop > 0 ? 'w-full' : 'w-[calc(100%-2rem)] md:w-[calc(100%-5rem)] lg:w-[min(calc(100%-5rem),1084px)] xxl:w-[1360px]'} `}
          ></div>,
          document.querySelector('.page-container-content'),
        )}
      </>
    )
  );
};

export default SelfbookBookingBarModule;
